import { useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import whatsappIcon from "@/assets/whatsapp-icon.png";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface ContactProps {
  variant?: "dark" | "light";
}

const schema = z.object({
  name: z.string().trim().min(2, "Name angeben").max(100),
  email: z.string().trim().email("Gültige E-Mail angeben").max(255),
  phone: z.string().trim().min(6, "Telefonnummer angeben").max(30),
  service: z.string().min(1, "Bitte Leistung wählen"),
  message: z.string().trim().min(5, "Bitte Projekt beschreiben").max(2000),
});

const SERVICE_LABEL: Record<string, string> = {
  fassade: "Fassadengestaltung",
  innen: "Innenarbeiten",
  badezimmer: "Badezimmer-Sanierung",
  oberflaechen: "Edle Oberflächen",
  boden: "Bodenveredelung",
  sonstiges: "Sonstiges",
};

export const Contact = ({ variant = "dark" }: ContactProps) => {
  const isDark = variant === "dark";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const contactInfo = [
    { icon: Phone, title: "Telefon", value: "05221 / 3832130", link: "tel:+4952213832130" },
    { icon: Phone, title: "Mobil", value: "0178 / 8783369", link: "tel:+491788783369" },
    { icon: Mail, title: "E-Mail", value: "info@malerfachbetrieb-stein.de", link: "mailto:info@malerfachbetrieb-stein.de" },
    { icon: MapPin, title: "Standort", value: "Mindener Str. 183, 32049 Herford", link: "https://maps.google.com/?q=Mindener+Str.+183,+32049+Herford" }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ name, email, phone, service, message });
    if (!parsed.success) {
      toast({
        title: "Bitte Eingaben prüfen",
        description: parsed.error.errors[0]?.message ?? "Ungültige Angaben.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.functions.invoke("send-contact-email", {
      body: {
        source: "kontaktformular",
        name: parsed.data.name,
        email: parsed.data.email,
        phone: parsed.data.phone,
        service: SERVICE_LABEL[parsed.data.service] ?? parsed.data.service,
        message: parsed.data.message,
      },
    });
    setSubmitting(false);
    if (error) {
      toast({
        title: "Senden fehlgeschlagen",
        description: "Bitte später erneut versuchen oder rufen Sie uns direkt an.",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Anfrage gesendet",
      description: "Wir melden uns innerhalb von 24 Stunden bei Ihnen.",
    });
    setName(""); setEmail(""); setPhone(""); setService(""); setMessage("");
  };

  return (
    <section
      id="kontakt"
      className={`relative py-32 overflow-hidden ${
        isDark ? "bg-foreground text-background" : "bg-background text-foreground"
      }`}
    >
      <div className="container mx-auto relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="font-caslon text-4xl md:text-6xl font-bold mb-6">
              Lassen Sie uns gemeinsam<br />Ihr Projekt verwirklichen
            </h2>
            <p className={`text-lg max-w-2xl mx-auto leading-relaxed ${isDark ? "text-background/85" : "text-muted-foreground"}`}>
              Schildern Sie uns Ihr Vorhaben – wir beraten Sie umfassend und erstellen
              Ihnen ein maßgeschneidertes Angebot
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            {/* Contact Form */}
            <Card className={`p-8 md:p-10 border-0 shadow-xl animate-fade-in-up delay-200 ${
              isDark ? "bg-background text-foreground" : "bg-foreground text-background"
            }`}>
              <h3 className="font-caslon text-2xl font-bold mb-6">Angebot anfragen</h3>

              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium mb-2 tracking-wide">Name *</label>
                    <Input
                      id="name"
                      placeholder="Ihr vollständiger Name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={100}
                      className={`rounded-lg ${isDark
                        ? "bg-muted/50 border-foreground/10 focus:border-foreground"
                        : "bg-background/10 border-background/20 focus:border-background text-background placeholder:text-background/80"
                      }`}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium mb-2 tracking-wide">E-Mail *</label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="ihre@email.de"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={255}
                      className={`rounded-lg ${isDark
                        ? "bg-muted/50 border-foreground/10 focus:border-foreground"
                        : "bg-background/10 border-background/20 focus:border-background text-background placeholder:text-background/80"
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs font-medium mb-2 tracking-wide">Telefon *</label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+49 123 456789"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={30}
                    className={`rounded-lg ${isDark
                      ? "bg-muted/50 border-foreground/10 focus:border-foreground"
                      : "bg-background/10 border-background/20 focus:border-background text-background placeholder:text-background/80"
                    }`}
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-xs font-medium mb-2 tracking-wide">Gewünschte Leistung *</label>
                  <select
                    id="service"
                    required
                    value={service}
                    onChange={(e) => setService(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-1 transition-all ${
                      isDark
                        ? "bg-muted/50 border-foreground/10 focus:border-foreground focus:ring-foreground"
                        : "bg-background/10 border-background/20 focus:border-background focus:ring-background text-background"
                    }`}
                  >
                    <option value="">Bitte wählen...</option>
                    <option value="fassade">Fassadengestaltung</option>
                    <option value="innen">Innenarbeiten</option>
                    <option value="badezimmer">Badezimmer-Sanierung</option>
                    <option value="oberflaechen">Edle Oberflächen</option>
                    <option value="boden">Bodenveredelung</option>
                    <option value="sonstiges">Sonstiges</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-medium mb-2 tracking-wide">Projektbeschreibung *</label>
                  <Textarea
                    id="message"
                    placeholder="Beschreiben Sie Ihr Vorhaben: Welche Räume/Flächen sollen bearbeitet werden? Besondere Wünsche?"
                    required
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={2000}
                    className={`resize-none rounded-lg ${
                      isDark
                        ? "bg-muted/50 border-foreground/10 focus:border-foreground"
                        : "bg-background/10 border-background/20 focus:border-background text-background placeholder:text-background/80"
                    }`}
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className={`w-full rounded-lg font-medium transition-all duration-300 hover:translate-y-[-1px] ${
                    isDark
                      ? "bg-foreground text-background hover:bg-foreground/90"
                      : "bg-background text-foreground hover:bg-background/90"
                  }`}
                >
                  {submitting ? "Wird gesendet…" : "Anfrage senden"}
                  <Send className="ml-2 h-4 w-4" />
                </Button>

                <p className={`text-[11px] text-center ${isDark ? "text-muted-foreground" : "text-background/80"}`}>
                  * Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten zu.
                </p>
              </form>
            </Card>

            {/* Contact Info */}
            <div className="space-y-4 animate-fade-in-up delay-300">
              <div className="space-y-3">
                {contactInfo.map((info, index) => (
                  <a
                    key={index}
                    href={info.link}
                    className={`block p-5 border rounded-xl transition-all duration-300 group ${
                      isDark
                        ? "bg-background/5 border-background/10 hover:bg-background/10"
                        : "bg-foreground/3 border-foreground/8 hover:bg-foreground/5"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2.5 rounded-lg group-hover:scale-105 transition-transform ${
                        isDark ? "bg-background/10" : "bg-foreground/5"
                      }`}>
                        <info.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className={`text-xs mb-0.5 ${isDark ? "text-background/80" : "text-muted-foreground"}`}>{info.title}</p>
                        <p className="font-semibold">{info.value}</p>
                      </div>
                    </div>
                  </a>
                ))}
              </div>

              <Card className={`p-8 border-0 ${isDark ? "bg-background text-foreground" : "bg-foreground text-background"}`}>
                <h3 className="font-caslon text-xl font-bold mb-3">Dringende Anfragen?</h3>
                <p className={`mb-6 text-sm ${isDark ? "text-muted-foreground" : "text-background/85"}`}>
                  Kontaktieren Sie uns gerne per WhatsApp oder telefonisch –
                  wir sind für Sie da!
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="tel:+491788783369"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all"
                    style={{ backgroundColor: '#16653426', color: '#15803d' }}
                  >
                    <Phone className="h-4 w-4" />
                    Jetzt anrufen
                  </a>
                  <a
                    href="https://wa.me/491788783369"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all"
                    style={{ backgroundColor: '#16653426', color: '#15803d' }}
                  >
                    <img src={whatsappIcon} alt="WhatsApp" className="h-4 w-4" />
                    WhatsApp
                  </a>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
